import React, { useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, KeyRound, Settings, LogOut, Sun, Moon, Monitor, Info, AlertTriangle, Wrench, X, Megaphone, Server, Radio, Cpu, DatabaseBackup, CalendarClock, Menu } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { cn, formatRelativeTime } from '../../lib/utils';
import { Avatar } from '../ui/Avatar';
import { useAuthStore } from '../../store/auth';
import { useThemeStore, resolveIsDark } from '../../store/theme';
import { getAnnouncements } from '../../api/platform';
import { listNotifications, markAllRead, deleteAllNotifications } from '../../api/notifications';
import type { ThemeMode } from '../../store/theme';
import type { Announcement } from '../../types';
import type { PanelNotification } from '../../api/notifications';

const SEEN_KEY = (userId: number) => `xcodec_seen_announcements_${userId}`;

function getSeenIds(userId: number): number[] {
  try {
    return JSON.parse(localStorage.getItem(SEEN_KEY(userId)) ?? '[]');
  } catch {
    return [];
  }
}

function saveSeenIds(userId: number, ids: number[]) {
  try {
    localStorage.setItem(SEEN_KEY(userId), JSON.stringify(ids));
  } catch {
    // Storage is unavailable (private mode, quota): the seen list is only a convenience.
  }
}

const typeIcon: Record<Announcement['type'], React.ReactNode> = {
  info: <Info size={13} className="text-blue-500 shrink-0" />,
  warning: <AlertTriangle size={13} className="text-amber-500 shrink-0" />,
  maintenance: <Wrench size={13} className="text-gray-500 dark:text-slate-400 shrink-0" />,
};

const notifKindIcon: Record<string, React.ReactNode> = {
  server_down:  <Server size={13} className="text-red-500 shrink-0" />,
  server_up:    <Server size={13} className="text-emerald-500 shrink-0" />,
  server_alert: <Cpu size={13} className="text-amber-500 shrink-0" />,
  stream_down:  <Radio size={13} className="text-red-500 shrink-0" />,
  stream_up:    <Radio size={13} className="text-emerald-500 shrink-0" />,
  backup_failed:  <DatabaseBackup size={13} className="text-red-500 shrink-0" />,
  backup_stale:   <DatabaseBackup size={13} className="text-red-500 shrink-0" />,
  backup_warning: <DatabaseBackup size={13} className="text-amber-500 shrink-0" />,
  epg_failed:     <CalendarClock size={13} className="text-red-500 shrink-0" />,
  epg_stale:      <CalendarClock size={13} className="text-amber-500 shrink-0" />,
};

// Notification links come from the server; only follow in-panel paths.
const isPanelPath = (link?: string): link is string =>
  !!link && link.startsWith('/') && !link.startsWith('//');

export function Topbar({ onMenu, menuOpen = false }: { onMenu?: () => void; menuOpen?: boolean }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { mode, setMode } = useThemeStore();
  const qc = useQueryClient();

  const [seenIds, setSeenIds] = useState<number[]>(() =>
    user ? getSeenIds(user.id) : []
  );

  const { data: announcements = [] } = useQuery({
    queryKey: ['announcements'],
    queryFn: getAnnouncements,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 2 * 60 * 1000,
    enabled: user?.role !== 'restreamer',
  });

  const isClientAdmin = user?.role === 'admin' || user?.role === 'client';

  const { data: panelNotifs = [] } = useQuery({
    queryKey: ['panel-notifications'],
    queryFn: listNotifications,
    refetchInterval: 30 * 1000,
    staleTime: 15 * 1000,
    enabled: isClientAdmin,
  });

  const markAllReadMut = useMutation({
    mutationFn: markAllRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['panel-notifications'] }),
  });

  const clearAllMut = useMutation({
    mutationFn: deleteAllNotifications,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['panel-notifications'] }),
  });

  const unseen = announcements.filter(a => !seenIds.includes(a.id));
  const unreadPanelNotifs = panelNotifs.filter((n: PanelNotification) => !n.read);
  const unreadCount = unseen.length + unreadPanelNotifs.length;

  const markAllSeen = useCallback(() => {
    if (!user) return;
    const allIds = announcements.map(a => a.id);
    setSeenIds(allIds);
    saveSeenIds(user.id, allIds);
    if (unreadPanelNotifs.length > 0) markAllReadMut.mutate();
  }, [user, announcements, unreadPanelNotifs.length]);

  const dismiss = useCallback((id: number) => {
    if (!user) return;
    const next = [...seenIds, id];
    setSeenIds(next);
    saveSeenIds(user.id, next);
  }, [user, seenIds]);

  const isDark = resolveIsDark(mode);

  const breadcrumbMap: Record<string, string> = {
    '/': t('nav.dashboard'),
    '/subscribers': t('subscribers.title'),
    '/subscribers/renew': t('renew_batch.title'),
    '/subscribers/quick': t('quick.page_line'),
    '/resellers': t('resellers.title'),

    '/packages': t('packages.title'),
    '/tickets': t('tickets.title'),
    '/servers': t('servers.title'),
    '/settings': t('nav.settings'),
    '/clients': t('nav.clients'),
    '/announcements': t('announcements.title'),
    '/health': 'System Health',
  };

  const pageTitle = breadcrumbMap[location.pathname] || 'xcodec';

  const themeOptions: { mode: ThemeMode; icon: React.ReactNode; label: string }[] = [
    { mode: 'light', icon: <Sun size={13} />, label: 'Light' },
    { mode: 'dark', icon: <Moon size={13} />, label: 'Dark' },
    { mode: 'system', icon: <Monitor size={13} />, label: 'System' },
  ];

  return (
    <header className="h-14 shrink-0 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700/60 flex items-center justify-between px-3 sm:px-5">
      <div className="flex items-center gap-2 min-w-0">
        <button
          type="button"
          onClick={onMenu}
          aria-label={t('nav.open_menu', 'Open menu')}
          aria-controls="app-sidebar"
          aria-expanded={menuOpen}
          className="lg:hidden h-9 w-9 -ms-1 rounded-lg flex items-center justify-center text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors shrink-0"
        >
          <Menu size={18} />
        </button>
        <span className="text-sm font-semibold text-gray-900 dark:text-slate-100 truncate">{pageTitle}</span>
      </div>

      <div className="flex items-center gap-1">
        {/* Theme toggle */}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button className="h-8 w-8 rounded-lg flex items-center justify-center text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors focus-visible:outline-none">
              {mode === 'dark' || (mode === 'system' && isDark) ? (
                <Moon size={15} />
              ) : (
                <Sun size={15} />
              )}
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              className="z-50 min-w-[130px] rounded-xl bg-white dark:bg-slate-800 shadow-lg border border-gray-200 dark:border-slate-700 py-1.5 animate-slide-up"
            >
              {themeOptions.map((opt) => (
                <DropdownMenu.Item
                  key={opt.mode}
                  className={cn(
                    'flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer focus:outline-none transition-colors',
                    mode === opt.mode
                      ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10'
                      : 'text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                  )}
                  onSelect={() => setMode(opt.mode)}
                >
                  {opt.icon}
                  {opt.label}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button className="relative h-8 w-8 rounded-lg flex items-center justify-center text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors focus-visible:outline-none">
              <Bell size={16} className={unreadCount > 0 ? 'text-indigo-500 dark:text-indigo-400' : ''} />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-indigo-500 border border-white dark:border-slate-900 text-[9px] font-bold text-white px-0.5">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              className="z-50 w-80 rounded-xl bg-white dark:bg-slate-800 shadow-lg border border-gray-200 dark:border-slate-700 animate-slide-up"
            >
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100 dark:border-slate-700">
                <span className="text-xs font-semibold text-gray-700 dark:text-slate-200 uppercase tracking-wide">Notifications</span>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllSeen}
                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    Mark all read
                  </button>
                )}
              </div>

              {panelNotifs.length === 0 && announcements.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-8 text-gray-400 dark:text-slate-500">
                  <Megaphone size={20} />
                  <span className="text-xs">No notifications</span>
                </div>
              ) : (
                <div className="max-h-80 overflow-y-auto">
                  {panelNotifs.map((n: PanelNotification) => (
                    <div
                      key={n.id}
                      role={isPanelPath(n.link) ? 'link' : undefined}
                      onClick={isPanelPath(n.link) ? () => navigate(n.link) : undefined}
                      className={cn(
                        'flex items-start gap-2.5 px-3 py-2.5 border-b border-gray-50 dark:border-slate-700/50 last:border-0',
                        isPanelPath(n.link) && 'cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/40',
                        n.read ? 'opacity-50' : 'bg-indigo-50/40 dark:bg-indigo-500/5'
                      )}
                    >
                      <span className="mt-0.5">{notifKindIcon[n.kind] ?? <Bell size={13} className="text-gray-400 shrink-0" />}</span>
                      <div className="flex-1 min-w-0">
                        <p className={cn('text-xs font-medium truncate', n.read ? 'text-gray-500 dark:text-slate-400' : 'text-gray-900 dark:text-slate-100')}>
                          {n.title}
                        </p>
                        {n.body && (
                          <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5 line-clamp-2">{n.body}</p>
                        )}
                        <p className="text-[10px] text-gray-300 dark:text-slate-600 mt-1">{formatRelativeTime(n.created_at)}</p>
                      </div>
                    </div>
                  ))}
                  {announcements.map(a => {
                    const seen = seenIds.includes(a.id);
                    return (
                      <div
                        key={`ann-${a.id}`}
                        className={cn(
                          'flex items-start gap-2.5 px-3 py-2.5 border-b border-gray-50 dark:border-slate-700/50 last:border-0',
                          seen ? 'opacity-50' : 'bg-indigo-50/40 dark:bg-indigo-500/5'
                        )}
                      >
                        <span className="mt-0.5">{typeIcon[a.type]}</span>
                        <div className="flex-1 min-w-0">
                          <p className={cn('text-xs font-medium truncate', seen ? 'text-gray-500 dark:text-slate-400' : 'text-gray-900 dark:text-slate-100')}>
                            {a.title}
                          </p>
                          {a.body && (
                            <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5 line-clamp-2">{a.body}</p>
                          )}
                          <p className="text-[10px] text-gray-300 dark:text-slate-600 mt-1">{formatRelativeTime(a.created_at)}</p>
                        </div>
                        {!seen && (
                          <button
                            onClick={() => dismiss(a.id)}
                            className="shrink-0 mt-0.5 text-gray-300 dark:text-slate-600 hover:text-gray-500 dark:hover:text-slate-400 transition-colors"
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="px-3 py-2 border-t border-gray-100 dark:border-slate-700 flex items-center justify-between">
                {panelNotifs.length > 0 ? (
                  <button
                    onClick={() => clearAllMut.mutate()}
                    className="text-xs text-red-500 dark:text-red-400 hover:underline"
                  >
                    Clear all alerts
                  </button>
                ) : <span />}
                {announcements.length > 0 && (
                  <button
                    onClick={() => navigate('/announcements')}
                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    View announcements
                  </button>
                )}
              </div>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button className="flex items-center gap-2 ml-1 h-8 rounded-lg px-2 text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors focus-visible:outline-none">
              {user && <Avatar name={user.username} size="xs" />}
              <span className="text-xs font-medium text-gray-700 dark:text-slate-300 max-w-[120px] truncate">
                {user?.username}
              </span>
              <ChevronDown size={12} className="text-gray-400 dark:text-slate-500" />
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              className={cn(
                'z-50 min-w-[180px] rounded-xl bg-white dark:bg-slate-800 shadow-lg border border-gray-200 dark:border-slate-700 py-1.5',
                'animate-slide-up'
              )}
            >
              {user && (
                <div className="px-3 py-2 border-b border-gray-100 dark:border-slate-700 mb-1">
                  <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">{user.username}</p>
                  <p className="text-xs text-gray-400 dark:text-slate-500">{user.email}</p>
                </div>
              )}

              {(user?.role === 'admin' || user?.role === 'client') && (
                <DropdownMenu.Item
                  className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer focus:outline-none"
                  onSelect={() => navigate('/settings')}
                >
                  <Settings size={14} className="text-gray-400 dark:text-slate-500" />
                  {t('nav.settings')}
                </DropdownMenu.Item>
              )}

              <DropdownMenu.Item
                className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer focus:outline-none"
                onSelect={() => navigate('/change-password')}
              >
                <KeyRound size={14} className="text-gray-400 dark:text-slate-500" />
                Change password
              </DropdownMenu.Item>

              <DropdownMenu.Separator className="my-1 border-t border-gray-100 dark:border-slate-700" />

              <DropdownMenu.Item
                className="flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 cursor-pointer focus:outline-none"
                onSelect={() => {
                  logout();
                  navigate('/login');
                }}
              >
                <LogOut size={14} />
                {t('nav.sign_out')}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}
